import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import LeftSideMenu from '../LeftSideMenu';
import { Formik, ErrorMessage } from 'formik';
import { Input, Form as AntdForm, AutoComplete, notification } from 'antd';
import * as Yup from 'yup';
import { Button } from '@ui/components';
import { connect } from 'react-redux';
import { metaSelectors } from '@ui/store/meta';
import { transformFormatSource } from '@ui/tools/transformSource';
import { createFactory, IFactory, updateFactory } from '@ui/api/v2/store.api';

const { Item } = AntdForm;

const initialValues = {
  name: '',
  country: '',
};

interface IProps {
  factory?: IFactory;
  countries: any[];
  onFactoryUpdate: (factory: IFactory) => void;
  onFactoryCreate: (factory: IFactory) => void;
  resetEditFactory: () => void;
}

const factorySchema = Yup.object().shape({
  name: Yup.string().required('Поле обязательно к заполнению'),
  country: Yup.string().required('Поле обязательно к заполнению'),
});

function FactoryForm({
  countries,
  factory,
  onFactoryCreate,
  onFactoryUpdate,
  resetEditFactory,
}: IProps) {
  const formikRef = useRef<any>(null);

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      if (factory && factory.id) {
        resetEditFactory();
        const response = await updateFactory(factory.id, values);
        if (response.data) {
          onFactoryUpdate(response.data);
        }
      } else {
        const { data, error } = await createFactory(values);
        if (data) {
          onFactoryCreate(data);
        } else {
          notification.error({
            message: error.response.data.message,
          });
        }
      }
      resetForm();
    },
    [factory, onFactoryCreate, onFactoryUpdate, resetEditFactory],
  );

  const cancelEditForm = useCallback(
    (_, { resetForm }) => {
      resetForm();
      resetEditFactory();
    },
    [resetEditFactory],
  );

  const dataSource = useMemo(() => transformFormatSource(countries), [
    countries,
  ]);

  useEffect(() => {
    if (formikRef.current && factory) {
      formikRef.current.setValues({
        name: factory.name,
        country: factory.country.id.toString(),
      });
    }
  }, [factory]);

  const title = useMemo(() => {
    if (factory && factory.id) {
      return 'Редактировать фабрику';
    }
    return 'Новая фабрика';
  }, [factory]);

  return (
    <LeftSideMenu className="markets__left-side" topPanel={title}>
      <Formik
        ref={formikRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onReset={cancelEditForm}
        validationSchema={factorySchema}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          setFieldValue,
          handleReset,
        }) => (
          <form onSubmit={handleSubmit} className="markets__form">
            <Item label="Имя">
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                size="large"
                type="text"
                name="name"
              />
              <ErrorMessage
                className="markets__form__error"
                name="name"
                component="div"
              />
            </Item>
            <Item label="Страна">
              <AutoComplete
                dataSource={dataSource}
                value={values.country}
                size="large"
                onSelect={value => {
                  setFieldValue('country', value);
                }}
              />
              <ErrorMessage
                name="country"
                className="markets__form__error"
                component="div"
              />
            </Item>
            <div className="markets__buttons-wrapper">
              <Item>
                <Button type="primary" htmlType="submit">
                  Сохранить
                </Button>
              </Item>
              <Item>
                <Button onClick={handleReset}>Отмена</Button>
              </Item>
            </div>
          </form>
        )}
      </Formik>
    </LeftSideMenu>
  );
}

export default connect(state => ({
  countries: metaSelectors.getCountries(state),
}))(memo(FactoryForm));



// WEBPACK FOOTER //
// ./src/components/Factories/FactoryForm.tsx